/**
 * OFFLINE BANNER
 * 
 * Displays banner when app is offline and shows sync status
 */

import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import { useOfflineQueue } from '../../hooks/useOfflineQueue';
import { WifiOff, CloudOff, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export function OfflineBanner() {
  const { isOffline } = useNetworkStatus();
  const { hasQueuedActions, queueSize, isSyncing } = useOfflineQueue();

  return (
    <AnimatePresence>
      {(isOffline || hasQueuedActions) && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed top-0 left-0 right-0 z-[9999]"
        >
          <div
            className="w-full border-b border-border font-mono text-[11px] tracking-[0.02em] uppercase"
            style={{
              background: isOffline
                ? 'var(--surface-2)'
                : 'var(--surface-3)',
            }}
          >
            <div 
              className="mx-auto flex items-center justify-center gap-3 py-2.5 px-4"
              style={{ width: 'min(90vw, 3000px)' }}
            >
              {isOffline ? (
                <>
                  <WifiOff className="w-3.5 h-3.5 text-foreground" />
                  <span className="text-foreground">
                    No Connection
                  </span>
                  {hasQueuedActions && (
                    <span className="text-muted-foreground">
                      • {queueSize} action{queueSize !== 1 ? 's' : ''} queued
                    </span>
                  )}
                </>
              ) : (
                <>
                  {isSyncing ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin text-[var(--brand)]" />
                      <span className="text-[var(--brand)]">
                        Syncing {queueSize} action{queueSize !== 1 ? 's' : ''}...
                      </span>
                    </>
                  ) : (
                    <>
                      <CloudOff className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        Sync complete
                      </span>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
